import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ListChecks, Plus, CheckCircle2, Trash2, Upload, CalendarDays, Loader2 } from 'lucide-react';
import { applicantTasks } from '@/lib/api';
import { toast } from 'sonner';

/**
 * ApplicantTasksCard — client-facing to-do list on the matter detail page.
 * Staff assigns tasks; the linked applicant sees them in their portal and can
 * mark them complete. Migration-manager staple.
 */
export default function ApplicantTasksCard({ matterId, applicantId }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [addOpen, setAddOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({ title: '', description: '', due_date: '', requires_upload: false });

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await applicantTasks.listForMatter(matterId); setItems(r.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, [matterId]);
  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.title.trim().length < 2) { toast.error('Task title too short'); return; }
    setSaving(true);
    try {
      await applicantTasks.create(matterId, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        due_date: form.due_date || null,
        requires_upload: form.requires_upload,
      });
      toast.success('Task assigned — visible in the applicant portal');
      setForm({ title: '', description: '', due_date: '', requires_upload: false });
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not assign task');
    } finally { setSaving(false); }
  };

  const toggleDone = async (t) => {
    try {
      await applicantTasks.patch(t.id, { status: t.status === 'done' ? 'pending' : 'done' });
      await load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await applicantTasks.delete(t.id); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const pendingCount = items.filter((t) => t.status !== 'done').length;

  return (
    <Card className="border-slate-200" data-testid="applicant-tasks-card">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ECC9A]/10">
              <ListChecks className="h-4 w-4 text-[#2ECC9A]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#1A3A5C]">Applicant to-do list</div>
              <div className="text-[11px] text-slate-500">
                {pendingCount === 0 ? 'No open items — well done' : `${pendingCount} open · visible in the applicant portal`}
              </div>
            </div>
          </div>
          {!applicantId ? (
            <span className="text-[11px] italic text-amber-600" data-testid="applicant-tasks-nolink">
              Link an applicant to assign tasks
            </span>
          ) : (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAddOpen((s) => !s)} data-testid="applicant-tasks-add-btn">
              <Plus className="mr-1 h-3 w-3" /> {addOpen ? 'Close' : 'Assign task'}
            </Button>
          )}
        </div>

        {addOpen && applicantId && (
          <div className="mb-3 space-y-2 rounded-lg border border-dashed border-[#2ECC9A]/40 bg-slate-50/50 p-3" data-testid="applicant-tasks-add-form">
            <Input
              placeholder="e.g. Upload passport bio page"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="applicant-tasks-title"
              maxLength={200}
            />
            <Input
              placeholder="Optional details or instructions…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="applicant-tasks-description"
              maxLength={2000}
            />
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <CalendarDays className="h-3 w-3" />
                <input type="date" value={form.due_date}
                       onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                       data-testid="applicant-tasks-due"
                       className="h-8 rounded-md border border-slate-200 px-2 text-xs" />
              </label>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={form.requires_upload}
                       onChange={(e) => setForm({ ...form, requires_upload: e.target.checked })}
                       data-testid="applicant-tasks-requires-upload" />
                Requires file upload
              </label>
              <Button size="sm" className="ml-auto h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs"
                      onClick={submit} disabled={saving} data-testid="applicant-tasks-submit">
                {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}Assign
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-[#2ECC9A]" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/40 p-4 text-center text-xs text-slate-500" data-testid="applicant-tasks-empty">
            No tasks assigned yet. Give your applicant a short, specific to-do and they&rsquo;ll see it in their portal.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="applicant-tasks-list">
            {items.map((t) => {
              const isDone = t.status === 'done';
              const overdue = t.due_date && !isDone && new Date(t.due_date) < new Date(new Date().toDateString());
              return (
                <div key={t.id} className={`flex items-start gap-2 rounded-md border p-2.5 text-xs transition-colors ${
                  isDone ? 'border-emerald-100 bg-emerald-50/40 opacity-75'
                  : overdue ? 'border-red-200 bg-red-50/40'
                  : 'border-slate-200 bg-white'}`} data-testid={`applicant-task-${t.id}`}>
                  <button type="button" onClick={() => toggleDone(t)}
                          data-testid={`applicant-task-toggle-${t.id}`}
                          className="mt-0.5 shrink-0" title={isDone ? 'Reopen' : 'Mark done'}>
                    <CheckCircle2 className={`h-4 w-4 ${isDone ? 'text-emerald-500' : 'text-slate-300 hover:text-[#2ECC9A]'}`} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold ${isDone ? 'line-through text-slate-500' : 'text-[#1A3A5C]'}`}>{t.title}</div>
                    {t.description && <div className="text-[11px] text-slate-500">{t.description}</div>}
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-400">
                      {t.due_date && (
                        <span className={overdue ? 'text-red-600 font-semibold' : ''}>
                          <CalendarDays className="mr-0.5 inline h-2.5 w-2.5" /> {t.due_date}{overdue ? ' · overdue' : ''}
                        </span>
                      )}
                      {t.requires_upload && (
                        <Badge className="border-[#2ECC9A]/30 bg-[#2ECC9A]/10 text-[#1A3A5C]" data-testid={`applicant-task-upload-${t.id}`}>
                          <Upload className="mr-0.5 h-2 w-2" /> upload
                        </Badge>
                      )}
                      <span>· assigned by {t.created_by}</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => remove(t)}
                          data-testid={`applicant-task-delete-${t.id}`}
                          className="text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
